/**
 * Les rendez-vous de dépôt.
 *
 * La boutique ne prend des dépôts que du mardi au vendredi, et la charge n'est
 * pas la même selon l'heure : le matin on déballe 50 à 100 pièces, l'après-midi
 * une douzaine tout au plus. Ces règles ne sont pas décoratives — un rendez-vous
 * de 60 pièces posé à 15 h bloque le comptoir jusqu'à la fermeture.
 *
 * L'application ne les impose pas : elle avertit. Un client qui insiste, un
 * créneau qu'on accepte quand même, cela existe ; un logiciel qui refuse en
 * silence fait perdre plus de temps qu'il n'en gagne.
 */
export const LIBELLE_STATUT_RDV = {
    prevu: 'Prévu',
    honore: 'Honoré',
    annule: 'Annulé',
    absent: 'Non venu',
};
/**
 * Jours d'ouverture de la boutique : mardi (2) à samedi (6).
 * Fermée le dimanche et le lundi.
 */
export const JOURS_OUVERTS = [2, 3, 4, 5, 6];
/**
 * Jours où l'on prend habituellement des dépôts : du mardi au vendredi. Le
 * samedi reste possible — cela arrive — mais c'est le jour de la clientèle,
 * et déballer trente pièces au milieu des acheteurs se paie cher. L'agenda le
 * signale sans l'interdire.
 */
export const JOURS_DEPOT = [2, 3, 4, 5];
/** Bascule matin / après-midi, en heure locale. */
export const HEURE_BASCULE = 13;
/**
 * Horaires d'ouverture. La boutique ferme une demi-heure plus tôt en juillet
 * et en août — les mois où l'on garde de la lumière mais pas de clients.
 */
export function horaires(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime()))
        return null;
    if (!JOURS_OUVERTS.includes(d.getDay()))
        return null;
    const ete = d.getMonth() === 6 || d.getMonth() === 7; // juillet, août
    return { ouverture: '10:00', fermeture: ete ? '18:00' : '18:30' };
}
/** « 10:00 – 18:30 », ou null les jours de fermeture. */
export function libelleHoraires(date) {
    const h = horaires(date);
    return h && `${h.ouverture} – ${h.fermeture}`;
}
/** Minutes depuis minuit — pour comparer une heure à un horaire. */
function minutes(heure) {
    const [h, m] = heure.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}
/** Nombre de pièces admis le matin, puis l'après-midi. */
export const PIECES_MATIN_MAX = 100;
export const PIECES_MATIN_MIN = 50;
export const PIECES_APRES_MIDI_MAX = 12;
/** Le créneau tombe-t-il le matin ? */
export function estMatin(debut) {
    const d = typeof debut === 'string' ? new Date(debut) : debut;
    return d.getHours() < HEURE_BASCULE;
}
/**
 * Ce qui cloche dans un rendez-vous, en clair. Liste vide = rien à signaler.
 * Ce sont des avertissements, jamais des refus.
 */
export function avertissementsRdv(debut, nbPieces) {
    const d = typeof debut === 'string' ? new Date(debut) : debut;
    if (Number.isNaN(d.getTime()))
        return ['Date invalide.'];
    const alertes = [];
    const ouverture = horaires(d);
    if (!ouverture) {
        alertes.push('La boutique est fermée le dimanche et le lundi.');
    }
    else {
        if (!JOURS_DEPOT.includes(d.getDay())) {
            alertes.push('Dépôt un samedi : jour de clientèle, à réserver aux petits volumes.');
        }
        const heure = d.getHours() * 60 + d.getMinutes();
        if (heure < minutes(ouverture.ouverture) || heure >= minutes(ouverture.fermeture)) {
            alertes.push(`Hors des heures d’ouverture (${ouverture.ouverture} – ${ouverture.fermeture}).`);
        }
    }
    if (nbPieces > 0) {
        if (estMatin(d)) {
            if (nbPieces > PIECES_MATIN_MAX) {
                alertes.push(`Plus de ${PIECES_MATIN_MAX} pièces : prévoir deux rendez-vous.`);
            }
        }
        else if (nbPieces > PIECES_APRES_MIDI_MAX) {
            alertes.push(`L’après-midi est limité à ${PIECES_APRES_MIDI_MAX} pièces — ${nbPieces} demandent une matinée.`);
        }
    }
    return alertes;
}
/** « mardi 28 juillet · 10:30 » — l'en-tête d'un rendez-vous. */
export function libelleCreneau(debut) {
    const d = new Date(debut);
    if (Number.isNaN(d.getTime()))
        return debut;
    const jour = new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(d);
    const heure = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(d);
    return `${jour} · ${heure}`;
}
//# sourceMappingURL=agenda.js.map